import { FC, useMemo } from 'react';
import { IonItem, IonLabel, IonList } from '@ionic/react';
import { Modals } from '../../../exports/Enums';
import LocationIcon from '../../../../../assets/icons/LocationIcon';
import useTheme from '../../../../../hooks/ui/useTheme';
import ClockIcon from '../../../../../assets/icons/order-methods/ClockIcon';
import HomeIcon from '../../../../../assets/icons/HomeIcon';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import useUserFriendlyScheduledFor from '../../../common/hooks/useUserFriendlyScheduledFor';
import { selectOrderMethodModalData } from '../../../../../store/slices/OrderMethodSlice';
import classnames from 'classnames';
import { StoreInterface } from '../../../../../store/types';
import { REDUX_STORE_SLICES } from '../../../../../services/exports/Constants';
import useDeliveryOrderMethodValidation from '../useDeliveryOrderMethodValidation';
import AddressHelpers from '../../../../../services/helpers/AddressHelpers';

interface Props {
  onClick: (id: Modals) => void;
  scheduledForError?: string;
  deliveryAddressError?: string;
}

const DeliverySegmentContent: FC<Props> = ({ onClick, scheduledForError, deliveryAddressError }) => {
  const theme = useTheme();
  const { t } = useTranslation(null, {
    keyPrefix: 'Modules:Mobile:Modals:OrderMethod:SegmentContents:DeliverySegment',
  });
  const { scheduledFor, deliveryData } = useSelector(selectOrderMethodModalData);
  const formattedScheduledFor = useUserFriendlyScheduledFor({ scheduledFor });
  const scheduledForLabel = scheduledForError ?? formattedScheduledFor;
  const companyName = useSelector((s: StoreInterface) => s[REDUX_STORE_SLICES.INITIAL_DATA]?.company?.name);
  const companyAddress = useSelector((s: StoreInterface) => s[REDUX_STORE_SLICES.INITIAL_DATA]?.company?.address);
  const { isAllDeliveryFieldsFilled } = useDeliveryOrderMethodValidation();
  const deliveryAddressLabel = useMemo(() => {
    if (deliveryAddressError) {
      return deliveryAddressError;
    }

    if (isAllDeliveryFieldsFilled) {
      return AddressHelpers.format({
        street_name: deliveryData.delivery_street_name,
        city: deliveryData.delivery_city,
        zip_code: deliveryData.delivery_zip_code,
        street_number: deliveryData.delivery_street_number,
      });
    }

    return t('subtitles.delivery_address');
  }, [deliveryAddressError, isAllDeliveryFieldsFilled, deliveryData]);

  return (
    <IonList className="w-full">
      <IonItem
        detail
        lines="full"
        className="ion-no-margin ion-no-padding"
        onClick={() => onClick(Modals.DeliveryAddress)}
      >
        <HomeIcon color={theme.color.brand.primary} className="w-[24px] h-[24px] mr-sm" />
        <IonLabel className="my-4">
          <h3 className={classnames('!font-semibold', { '!text-danger': !!deliveryAddressError })}>
            {t('titles.delivery_address')}
          </h3>
          <p className={classnames('font-medium !text-xs')}>{deliveryAddressLabel}</p>
        </IonLabel>
      </IonItem>
      <IonItem detail lines="full" className="ion-no-margin ion-no-padding" onClick={() => onClick(Modals.Location)}>
        <LocationIcon type={2} color={theme.color.brand.primary} className="w-[24px] h-[24px] mr-sm" />
        <IonLabel className="my-4">
          <h3 className="!font-semibold">{t('titles.location')}</h3>
          <p className="font-medium !text-xs capitalize">{`${companyName}, ${companyAddress}`}</p>
        </IonLabel>
      </IonItem>
      <IonItem detail lines="full" className="ion-no-margin ion-no-padding" onClick={() => onClick(Modals.DateTime)}>
        <ClockIcon color={theme.color.brand.primary} className="w-[24px] h-[24px] mr-sm" />
        <IonLabel className="my-4">
          <h3 className={classnames('!font-semibold', { '!text-danger': !!scheduledForError })}>
            {t('titles.date_time')}
          </h3>
          <p className={classnames('font-medium !text-xs')}>{scheduledForLabel}</p>
        </IonLabel>
      </IonItem>
    </IonList>
  );
};

export default DeliverySegmentContent;
